import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TEMA } from '../theme';
import {
  AttÅtgärda, Avvikelse, DubblettPunkt, LeverantörPunkt, PrisavvikelsePunkt, Producent, RadPunkt, TillverkarePunkt, prisavvikelseSett,
  hämtaAttÅtgärda, hämtaProducenter, ignoreraLeverantör, ignoreraTillverkare, kopplaLeverantör, ingenArtikel, inteDubblett, inteSammaRad,
  kopplaRad, kopplaTillverkare, ångraAutomatisk, skapaProducent, svaraAvvikelse, datumText, felText, valutabelopp,
} from '../api/inkop';
import { sida, rubrik, underrubrik, kort, fält, knapp, knappSpök, länkknapp, märke } from '../components/inkopStil';

// Sidan visar det som inte kunde matchas säkert. Ingenting här kopplas av sig
// självt: ett förslag blir en koppling först när någon klickar. Varje svar
// loggas, och det som besvarats kan öppnas igen längst ner.

const RADER_PER_SIDA = 25;

const text: React.CSSProperties = { fontSize: '13.5px', color: TEMA.ink, lineHeight: 1.5 };
const svag: React.CSSProperties = { fontSize: '12.5px', color: TEMA.inkFaint };
const mono: React.CSSProperties = { fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' };
const punkt: React.CSSProperties = { padding: '14px 0', borderTop: `1px solid ${TEMA.border}` };
const knapprad: React.CSSProperties = { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px' };
const liten: React.CSSProperties = { ...knappSpök, padding: '5px 10px', fontSize: '12.5px' };
const litenGuld: React.CSSProperties = { ...knapp, padding: '5px 10px', fontSize: '12.5px' };

function meddelaMenyn() {
  window.dispatchEvent(new Event('vinsales:att-atgarda-andrad'));
}

// "Ignorera" och liknande svar tar en frivillig kommentar innan de sparas.
function MedKommentar({ etikett, platshållare, onSvar }: {
  etikett: string; platshållare: string; onSvar: (kommentar: string) => Promise<void>;
}) {
  const [öppen, setÖppen] = useState(false);
  const [kommentar, setKommentar] = useState('');
  const [sparar, setSparar] = useState(false);
  if (!öppen) return <button style={liten} onClick={() => setÖppen(true)}>{etikett}</button>;
  return (
    <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        autoFocus style={{ ...fält, width: '240px', padding: '5px 8px', fontSize: '12.5px' }}
        placeholder={platshållare} value={kommentar} onChange={e => setKommentar(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') setÖppen(false); }}
      />
      <button style={liten} disabled={sparar} onClick={async () => {
        setSparar(true);
        try { await onSvar(kommentar); } finally { setSparar(false); }
      }}>Spara</button>
      <button style={länkknapp} onClick={() => setÖppen(false)}>Avbryt</button>
    </span>
  );
}

function Sektion({ titel, antal, beskrivning, children }: {
  titel: string; antal: number; beskrivning: string; children: React.ReactNode;
}) {
  return (
    <section style={kort}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ ...underrubrik, margin: 0 }}>{titel}</h2>
        <span style={märke(antal ? 'guld' : 'grå')}>{antal}</span>
      </div>
      <p style={{ ...svag, margin: '6px 0 4px', maxWidth: '720px', lineHeight: 1.5 }}>{beskrivning}</p>
      {antal === 0 ? <p style={{ ...svag, margin: '10px 0 0' }}>Inget att åtgärda.</p> : children}
    </section>
  );
}

function ImportPunkt({ a, svara }: { a: Avvikelse; svara: (f: () => Promise<unknown>) => Promise<void> }) {
  return (
    <div style={punkt}>
      <div style={text}>{a.text}</div>
      <div style={knapprad}>
        {a.inkop_id && <Link to={`/inkop/lista/${a.inkop_id}`} style={{ ...länkknapp, textDecoration: 'none' }}>Öppna inköpet →</Link>}
        <MedKommentar etikett="Klart" platshållare="Vad gjordes? (frivilligt)"
          onSvar={k => svara(() => svaraAvvikelse(a.id, 'hanterad', k))} />
        <MedKommentar etikett="Behövs inte" platshållare="Varför? (frivilligt)"
          onSvar={k => svara(() => svaraAvvikelse(a.id, 'ignorerad', k))} />
      </div>
    </div>
  );
}

function Dubblett({ d, svara }: { d: DubblettPunkt; svara: (f: () => Promise<unknown>) => Promise<void> }) {
  const [a, b] = d.inkop;
  return (
    <div style={punkt}>
      <div style={text}>{d.producent}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '10px', marginTop: '8px' }}>
        {d.inkop.map(i => (
          <Link key={i.id} to={`/inkop/lista/${i.id}`} style={{
            display: 'block', textDecoration: 'none', border: `1px solid ${TEMA.border}`, borderRadius: '3px', padding: '10px 12px',
          }}>
            <div style={{ ...text, display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <span>{i.rubrik || (i.bestalld ? `Beställt ${datumText(i.bestalld)}` : 'Utan rubrik')}</span>
              <span style={märke('grå')}>{i.statusnamn}</span>
            </div>
            <div style={{ ...svag, marginTop: '4px' }}>
              {i.flaskor} fl · {valutabelopp(i.belopp_valuta, i.valuta, 0)}
              {i.import_kalla ? ` · importerat från ${i.import_kalla}` : ''}
            </div>
          </Link>
        ))}
      </div>
      <div style={knapprad}>
        <MedKommentar etikett="Olika inköp" platshållare="Varför? (frivilligt)"
          onSvar={k => svara(() => inteDubblett(a.id, b.id, k))} />
        <span style={svag}>Är det samma inköp: öppna det ena och ta bort det. Det som tas bort sparas i händelseloggen.</span>
      </div>
    </div>
  );
}

// Ett vin i ett inköp som inte är kopplat till någon artikel i Fortnox. Överst
// vinet som det står i inköpet (ofta från Excel), under det artiklarna i
// Fortnox som kan vara samma vin. Priset i inköpet är bara en uppgift; listpriset
// i Fortnox gäller.
function Rad({ r, svara }: { r: RadPunkt; svara: (f: () => Promise<unknown>) => Promise<void> }) {
  const [eget, setEget] = useState('');
  return (
    <div style={{ ...punkt, paddingLeft: '12px' }}>
      <div style={text}>
        {r.vin}{r.argang && !r.vin.includes(r.argang) ? ` ${r.argang}` : ''}
        <span style={svag}>
          {' '}· {r.volym_cl ?? 75} cl · {r.antal ?? 0} fl
          {r.listpris ? ` · ${Math.round(r.listpris)} kr i ${r.kalla ? 'Excel' : 'inköpet'}` : ''}
          {r.angivet_artikel_nr ? ` · nummer ${r.angivet_artikel_nr} finns inte i Fortnox` : ''}
        </span>
      </div>
      {(r.dubbletter || []).length > 1 && (
        <div style={{ ...svag, color: TEMA.accent, marginTop: '6px' }}>
          {r.dubbletter.length} artiklar i Fortnox ser ut att vara samma vin ({r.dubbletter.join(', ')}). Koppla den som gäller,
          och se över de andra i Fortnox.
        </div>
      )}
      {r.kandidater.length > 0 ? (
        <div style={{ marginTop: '8px' }}>
          <div style={svag}>{r.bland_producentens ? 'Kan vara samma vin i Fortnox:' : 'Producenten har inga artiklar i Fortnox. Kan vara samma vin:'}</div>
          {r.kandidater.map(k => (
            <div key={k.artikel_nr} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
              <span style={{ ...mono, color: TEMA.inkSoft, width: '86px' }}>{k.artikel_nr}</span>
              <span style={{ ...text, flex: '1 1 220px', color: k.annan_argang ? TEMA.inkSoft : TEMA.ink }}>
                {k.namn}
                <span style={svag}> · {k.pris} kr i Fortnox{k.lager != null ? ` · lager ${Math.round(k.lager)}` : ''}</span>
                {k.annan_argang && <span style={{ ...märke('grå'), marginLeft: '8px' }}>annan årgång</span>}
              </span>
              {/* Knapparna radbryts tillsammans, så att Inte samma aldrig hamnar ensam under Koppla. */}
              <span style={{ display: 'flex', gap: '8px', marginLeft: 'auto', flexShrink: 0 }}>
                <button style={k.annan_argang ? liten : litenGuld} onClick={() => svara(() => kopplaRad(r.rad_id, k.artikel_nr))}>Koppla</button>
                <button style={liten} onClick={() => svara(() => inteSammaRad(r.rad_id, k.artikel_nr))}>Inte samma</button>
              </span>
            </div>
          ))}
        </div>
      ) : <div style={{ ...svag, marginTop: '6px' }}>Inget vin i Fortnox liknar det här. Finns det under ett annat namn, skriv in artikelnumret.</div>}
      <div style={knapprad}>
        <input style={{ ...fält, width: '140px', padding: '5px 8px', fontSize: '12.5px' }} placeholder="Artikelnummer"
          value={eget} onChange={e => setEget(e.target.value)} />
        <button style={liten} disabled={!eget.trim()} onClick={() => svara(() => kopplaRad(r.rad_id, eget.trim()))}>Koppla</button>
        <MedKommentar etikett="Finns inte i Fortnox" platshållare="Varför? T.ex. prov, olja, såldes aldrig (frivilligt)"
          onSvar={k => svara(() => ingenArtikel(r.rad_id, k))} />
      </div>
    </div>
  );
}

function RaderPerInköp({ rader, svara }: { rader: RadPunkt[]; svara: (f: () => Promise<unknown>) => Promise<void> }) {
  const [visa, setVisa] = useState(RADER_PER_SIDA);
  const grupper = useMemo(() => {
    const g: { inkop_id: string; inkop: string; rader: RadPunkt[] }[] = [];
    rader.slice(0, visa).forEach(r => {
      const sista = g[g.length - 1];
      if (sista && sista.inkop_id === r.inkop_id) sista.rader.push(r);
      else g.push({ inkop_id: r.inkop_id, inkop: r.inkop, rader: [r] });
    });
    return g;
  }, [rader, visa]);
  return (
    <>
      {grupper.map(g => (
        <div key={g.inkop_id} style={{ marginTop: '14px' }}>
          <Link to={`/inkop/lista/${g.inkop_id}`} style={{ ...mono, color: TEMA.accent, textDecoration: 'none' }}>{g.inkop} →</Link>
          <span style={{ ...svag, marginLeft: '10px' }}>
            {[g.rader[0].bestalld ? `beställt ${datumText(g.rader[0].bestalld)}` : null, g.rader[0].kalla ? `från ${g.rader[0].kalla}` : null].filter(Boolean).join(' · ')}
          </span>
          {g.rader.map(r => <Rad key={r.rad_id} r={r} svara={svara} />)}
        </div>
      ))}
      {rader.length > visa && (
        <button style={{ ...liten, marginTop: '14px' }} onClick={() => setVisa(v => v + RADER_PER_SIDA)}>
          Visa fler ({rader.length - visa} kvar)
        </button>
      )}
    </>
  );
}

function Tillverkare({ t, producenter, svara }: {
  t: TillverkarePunkt; producenter: Producent[]; svara: (f: () => Promise<unknown>) => Promise<void>;
}) {
  const [vald, setVald] = useState('');
  return (
    <div style={punkt}>
      <div style={text}>
        {t.tillverkare} <span style={svag}>· {t.antal_artiklar} {t.antal_artiklar === 1 ? 'artikel' : 'artiklar'} i lager</span>
      </div>
      <div style={{ ...svag, marginTop: '4px' }}>
        {t.artiklar.map(a => `${a.artikel_nr} ${a.beskrivning}`).join(' · ')}{t.antal_artiklar > t.artiklar.length ? ' …' : ''}
      </div>
      <div style={knapprad}>
        {t.forslag && (
          <button style={litenGuld} onClick={() => svara(() => kopplaTillverkare(t.tillverkare, t.forslag!.id))}>
            Koppla till {t.forslag.namn}
          </button>
        )}
        <select style={{ ...fält, width: '220px', padding: '5px 8px', fontSize: '12.5px' }} value={vald} onChange={e => setVald(e.target.value)}>
          <option value="">{t.forslag ? 'Annan producent…' : 'Välj producent…'}</option>
          {producenter.map(p => <option key={p.id} value={p.id}>{p.namn}</option>)}
        </select>
        <button style={liten} disabled={!vald} onClick={() => svara(() => kopplaTillverkare(t.tillverkare, vald))}>Koppla</button>
        <button style={liten} onClick={() => svara(() => skapaProducent({ namn: t.tillverkare, fortnox_namn: [t.tillverkare] }))}>
          Ny producent
        </button>
        <MedKommentar etikett="Hör inte till någon" platshållare="Varför? T.ex. köps via agent (frivilligt)"
          onSvar={k => svara(() => ignoreraTillverkare(t.tillverkare, k))} />
      </div>
    </div>
  );
}

// Ett vin vars pris i Fortnox inte är det ni låste. Fortnox gäller; punkten är
// till för att avvikelsen ska ses.
function Prisavvikelse({ a, svara }: { a: PrisavvikelsePunkt; svara: (f: () => Promise<unknown>) => Promise<void> }) {
  const högre = a.fortnox_listpris > a.last_listpris;
  return (
    <div style={punkt}>
      <div style={text}>
        <Link to={`/inkop/prissattning?producent=${a.producent_id}`} style={{ color: TEMA.ink, textDecoration: 'none' }}>{a.producent}</Link>
        {' · '}{a.vin}{a.argang && a.vin && !a.vin.includes(a.argang) ? ` ${a.argang}` : ''}
        {a.artikel_nr && <span style={svag}> · {a.artikel_nr}</span>}
      </div>
      <div style={{ ...svag, marginTop: '4px' }}>
        Låst {Math.round(a.last_listpris)} kr{a.last_marginal != null ? ` (${a.last_marginal.toLocaleString('sv-SE')} %)` : ''} ·{' '}
        <span style={{ color: TEMA.ink }}>Fortnox {Math.round(a.fortnox_listpris)} kr</span>
        {a.fortnox_marginal != null ? ` (${a.fortnox_marginal.toLocaleString('sv-SE')} %)` : ''}
        <span style={{ color: högre ? TEMA.positive : '#D9695F' }}> · {högre ? '+' : ''}{Math.round(a.fortnox_listpris - a.last_listpris)} kr</span>
      </div>
      <div style={knapprad}>
        <MedKommentar etikett="Sett, Fortnox gäller" platshållare="Varför blev det så? (frivilligt)"
          onSvar={k => svara(() => prisavvikelseSett(a.nyckel, a.rad_id, k))} />
        <span style={svag}>Är priset i Fortnox fel, ändra det i Fortnox.</span>
      </div>
    </div>
  );
}

// En leverantör i Fortnox som kan vara en producent. När den kopplas blir
// dess fakturor producentens betalningar på inköpen.
function Leverantör({ l, producenter, svara }: {
  l: LeverantörPunkt; producenter: Producent[]; svara: (f: () => Promise<unknown>) => Promise<void>;
}) {
  const [vald, setVald] = useState('');
  return (
    <div style={punkt}>
      <div style={text}>
        {l.namn || 'Namn saknas'} <span style={svag}>· leverantör {l.leverantor_nr} i Fortnox</span>
      </div>
      <div style={{ ...svag, marginTop: '4px' }}>
        {l.antal_fakturor} {l.antal_fakturor === 1 ? 'faktura' : 'fakturor'} i {l.valutor.join(', ')}
        {l.senaste ? ` · senast ${datumText(l.senaste)}` : ''}
      </div>
      <div style={knapprad}>
        {l.forslag && (
          <button style={litenGuld} onClick={() => svara(() => kopplaLeverantör(l.leverantor_nr, l.namn, l.forslag!.id))}>
            Koppla till {l.forslag.namn}
          </button>
        )}
        <select style={{ ...fält, width: '220px', padding: '5px 8px', fontSize: '12.5px' }} value={vald} onChange={e => setVald(e.target.value)}>
          <option value="">{l.forslag ? 'Annan producent…' : 'Välj producent…'}</option>
          {producenter.map(p => <option key={p.id} value={p.id}>{p.namn}</option>)}
        </select>
        <button style={liten} disabled={!vald} onClick={() => svara(() => kopplaLeverantör(l.leverantor_nr, l.namn, vald))}>Koppla</button>
        <MedKommentar etikett="Ingen producent" platshållare="T.ex. speditör eller agent (frivilligt)"
          onSvar={k => svara(() => ignoreraLeverantör(l.leverantor_nr, l.namn, k))} />
      </div>
    </div>
  );
}

const STATUSTEXT: Record<Avvikelse['status'], string> = { oppen: 'Öppen', hanterad: 'Hanterad', ignorerad: 'Ignorerad' };

// Det som kopplades automatiskt, för att kunna se över det och ångra det som blev fel.
const AUTOTYP: Record<string, string> = { tillverkare: 'Tillverkare', rad: 'Inköpsrad', leverantor: 'Leverantör' };
function Automatiskt({ lista, svara }: { lista: Avvikelse[]; svara: (f: () => Promise<unknown>) => Promise<void> }) {
  const [öppen, setÖppen] = useState(false);
  if (!lista.length) return null;
  return (
    <section style={{ ...kort, background: 'transparent' }}>
      <button style={{ ...länkknapp, color: TEMA.inkSoft }} onClick={() => setÖppen(o => !o)}>
        {öppen ? '▾' : '▸'} Kopplat automatiskt ({lista.length})
      </button>
      {öppen && (
        <p style={{ ...svag, margin: '8px 0 0', maxWidth: '720px', lineHeight: 1.5 }}>
          Kopplat för att det var säkert: samma namn hos exakt en producent, tillverkarens artiklar på en producents inköp,
          eller ett vin som är klart bästa träff bland producentens egna artiklar med samma årgång. Ångra det som blev fel;
          punkten kommer då tillbaka här och kopplas aldrig automatiskt igen.
        </p>
      )}
      {öppen && lista.map(a => (
        <div key={a.id} style={{ ...punkt, display: 'flex', gap: '12px', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={märke('grön')}>{AUTOTYP[a.typ] || a.typ}</span>
          <span style={{ ...text, flex: '1 1 300px' }}>{a.text}</span>
          <span style={svag}>{datumText(a.hanterad_tid?.slice(0, 10))}</span>
          <MedKommentar etikett="Ångra" platshållare="Vad blev fel? (frivilligt)"
            onSvar={k => svara(() => ångraAutomatisk(a.id, k))} />
        </div>
      ))}
    </section>
  );
}

function Besvarade({ lista, svara }: { lista: Avvikelse[]; svara: (f: () => Promise<unknown>) => Promise<void> }) {
  const [öppen, setÖppen] = useState(false);
  if (!lista.length) return null;
  return (
    <section style={{ ...kort, background: 'transparent' }}>
      <button style={{ ...länkknapp, color: TEMA.inkSoft }} onClick={() => setÖppen(o => !o)}>
        {öppen ? '▾' : '▸'} Besvarade ({lista.length} senaste)
      </button>
      {öppen && lista.map(a => (
        <div key={a.id} style={{ ...punkt, display: 'flex', gap: '12px', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={märke(a.status === 'hanterad' ? 'grön' : 'grå')}>{STATUSTEXT[a.status]}</span>
          <span style={{ ...text, flex: '1 1 300px' }}>
            {a.text}{a.kommentar ? <span style={svag}> · {a.kommentar}</span> : null}
          </span>
          <span style={svag}>{a.hanterad_av_namn || ''} {datumText(a.hanterad_tid?.slice(0, 10))}</span>
          <button style={länkknapp} onClick={() => svara(() => svaraAvvikelse(a.id, 'oppen'))}>Öppna igen</button>
        </div>
      ))}
    </section>
  );
}

export default function InkopAttAtgarda() {
  const [data, setData] = useState<AttÅtgärda | null>(null);
  const [producenter, setProducenter] = useState<Producent[]>([]);
  const [fel, setFel] = useState<string | null>(null);

  const ladda = useCallback(() => {
    return hämtaAttÅtgärda().then(r => setData(r.data)).catch(e => setFel(felText(e, 'Kunde inte hämta listan.')));
  }, []);

  useEffect(() => {
    ladda();
    hämtaProducenter().then(r => setProducenter(r.data)).catch(() => {});
  }, [ladda]);

  const svara = useCallback(async (f: () => Promise<unknown>) => {
    setFel(null);
    try {
      await f();
      await ladda();
      hämtaProducenter().then(r => setProducenter(r.data)).catch(() => {});
      meddelaMenyn();
    } catch (e) {
      setFel(felText(e, 'Det gick inte att spara.'));
    }
  }, [ladda]);

  return (
    <div style={sida}>
      <h1 style={{ ...rubrik, marginBottom: '8px' }}>Att åtgärda</h1>
      <p style={{ ...svag, fontSize: '13.5px', margin: '0 0 22px', maxWidth: '720px', lineHeight: 1.5 }}>
        Det som inte kunde matchas säkert. Det som är säkert kopplas automatiskt och syns längst ner, där det
        går att ångra. Ingenting försvinner tyst: varje koppling och svar sparas i händelseloggen.
      </p>
      {fel && <p style={{ color: '#D9695F', fontSize: '13.5px' }}>{fel}</p>}
      {data?.automatik && (data.automatik.tillverkare + data.automatik.rader > 0 || (data.automatik.fel || []).length > 0) && (
        <div style={{ ...svag, margin: '-10px 0 18px' }}>
          {data.automatik.tillverkare + data.automatik.rader > 0 && (
            <span style={{ color: TEMA.positive }}>
              Nu kopplades {[data.automatik.tillverkare ? `${data.automatik.tillverkare} tillverkare` : null,
                data.automatik.rader ? `${data.automatik.rader} ${data.automatik.rader === 1 ? 'vin' : 'viner'}` : null].filter(Boolean).join(' och ')} automatiskt.
              {' '}Se Kopplat automatiskt längst ner.
            </span>
          )}
          {(data.automatik.fel || []).length > 0 && (
            <div style={{ color: '#D9695F', marginTop: '4px' }}>
              Automatiken stötte på fel: {(data.automatik.fel || []).slice(0, 3).join(' · ')}
            </div>
          )}
        </div>
      )}
      {!data && !fel && <p style={svag}>Laddar...</p>}
      {data && (
        <>
          {data.import.length > 0 && (
            <Sektion titel="Från läglistan" antal={data.import.length}
              beskrivning="Det som läglistan säger om pågående inköp men som inte kunde läsas in säkert.">
              {data.import.map(a => <ImportPunkt key={a.id} a={a} svara={svara} />)}
            </Sektion>
          )}
          <Sektion titel="Möjliga dubbletter" antal={data.dubbletter.length}
            beskrivning="Två inköp från samma producent med lika många flaskor och samma belopp.">
            {data.dubbletter.map(d => <Dubblett key={d.nyckel} d={d} svara={svara} />)}
          </Sektion>
          <Sektion titel="Viner som inte är kopplade till Fortnox" antal={data.rader.length}
            beskrivning="Viner i inköp som har kommit in, där Vinsales inte vet vilken artikel i Fortnox det är. Viner från Excel-prismodellen visas inte; Fortnox är sanningen. Utan kopplingen syns inte lager och försäljning för vinet. Välj rätt artikel, eller svara Finns inte i Fortnox. Det som är säkert kopplas automatiskt.">
            <RaderPerInköp rader={data.rader} svara={svara} />
          </Sektion>
          <Sektion titel="Artiklar utan producent" antal={data.tillverkare.length}
            beskrivning="Tillverkare i affärssystemet, på artiklar som finns i lager, som inte hör till något producentkort. En koppling gäller alla tillverkarens artiklar, nu och framöver.">
            {data.tillverkare.map(t => <Tillverkare key={t.nyckel} t={t} producenter={producenter} svara={svara} />)}
          </Sektion>
          {(data.prisavvikelser || []).length > 0 && (
            <Sektion titel="Pris i Fortnox avviker från det ni låste" antal={data.prisavvikelser.length}
              beskrivning="Viner där listpriset i Fortnox inte är det pris ni kom överens om och låste i Prissättning. Fortnox gäller alltid; här syns avvikelsen så att den inte går förbi.">
              {data.prisavvikelser.map(a => <Prisavvikelse key={a.nyckel} a={a} svara={svara} />)}
            </Sektion>
          )}
          {(data.leverantorer || []).length > 0 && (
            <Sektion titel="Leverantörer utan producent" antal={data.leverantorer.length}
              beskrivning="Leverantörer i Fortnox som liknar en producent eller fakturerar i utländsk valuta. Betalningarna på inköpen läses från producentens fakturor, så leverantören behöver höra till rätt producent.">
              {data.leverantorer.map(l => <Leverantör key={l.nyckel} l={l} producenter={producenter} svara={svara} />)}
            </Sektion>
          )}
          <Automatiskt lista={data.automatiskt || []} svara={svara} />
          <Besvarade lista={data.besvarade} svara={svara} />
        </>
      )}
    </div>
  );
}
